import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AppFooter from "../components/AppFooter";

const TermsAndConditions = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Introduction",
      content: `Welcome to AICA Health (“AICA”, “Company”, “we”, “our”, or “us”).

These Terms govern access to and use of:
aicahealth.com, apps, AI systems, devices, APIs, dashboards, analytics, and all related services.

By using the Platform, you agree to these Terms.`,
    },

    {
      title: "2. Nature of Services",
      content: `AICA provides AI-enabled preventive healthcare support including wellness monitoring, risk indication, symptom tracking, and clinical workflow support.

The Platform uses AI, machine learning, predictive analytics, optical sensing, and multimodal systems.`,
    },

    {
      title: "3. Important Medical Disclaimer",
      content: `THE PLATFORM DOES NOT provide diagnosis, treatment, emergency care, prescriptions, or clinical decision-making.

All outputs are informational, assistive, and probabilistic.

Users must always consult licensed healthcare professionals.`,
    },

    {
      title: "4. Human-in-the-Loop Responsibility",
      content: `All clinical decisions including diagnosis, treatment, escalation, and prescriptions remain solely with licensed healthcare professionals.

AICA is only an assistive tool and does not replace medical judgment.`,
    },

    {
      title: "5. No Sole Reliance",
      content: `Users must not rely solely on Platform outputs for diagnosis, treatment, emergency response, or medical decisions.`,
    },

    {
      title: "6. Eligibility",
      content: `Users must be 18+ or use the Platform under lawful guardian consent.`,
    },

    {
      title: "7. Data Consent",
      content: `Users consent to collection and processing of demographic, physiological, biometric, behavioral, and medical data for AI analysis and improvement.`,
    },

    {
      title: "8. AI & Automated Outputs",
      content: `AI outputs may be incomplete, inaccurate, or probabilistic and must not be used as medical authority.`,
    },

    {
      title: "9. Physiological Inference",
      content: `Outputs are inference-based and not clinical diagnosis or medical certification.`,
    },

    {
      title: "10. Investigational Features",
      content: `Some features may be pilot-stage, experimental, or under validation.`,
    },

    {
      title: "11. Enterprise Deployments",
      content: `Hospitals, enterprises, and institutions are responsible for consent, compliance, and clinical decisions.

AICA is not responsible for clinical misuse or outcomes.`,
    },

    { 
      title: "12. User Responsibilities",
      content: `Users must not misuse, reverse engineer, or manipulate the Platform or use it for unlawful purposes.`,
    },

    {
      title: "13. Intellectual Property",
      content: `All software, models, systems, analytics, and content belong exclusively to AICA HEALTH TECH Pvt Ltd.`,
    },

    {
      title: "14. Data & Model Improvement Rights",
      content: `AICA may anonymize and use data for AI improvement, analytics, research, and system optimization.`,
    },

    {
      title: "15. Privacy & DPDP Compliance",
      content: `Data is processed under Indian law and Digital Personal Data Protection Act, 2023.`,
    },

    {
      title: "16. Cross-Border Processing",
      content: `Data may be stored or processed in India or internationally using secure cloud systems.`,
    },

    {
      title: "17. Security Disclaimer",
      content: `No system is 100% secure. Users accept inherent risks of digital healthcare systems.`,
    },

    {
      title: "18. Third-Party Services",
      content: `AICA integrates with hospitals, labs, insurers, APIs, and cloud services and is not responsible for third-party failures.`,
    },

    {
      title: "19. Limitation of Liability",
      content: `AICA is not liable for indirect, incidental, medical, or consequential damages or clinical outcomes.`,
    },

    {
      title: "20. Indemnity",
      content: `Users agree to indemnify AICA against misuse, legal violations, or unauthorized data usage.`,
    },

    {
      title: "21. Regulatory Positioning",
      content: `AICA is not a medical device unless explicitly stated. Features may be investigational.`,
    },

    {
      title: "22. Termination",
      content: `Access may be suspended for misuse, legal compliance, or security concerns.`,
    },

    {
      title: "23. Governing Law",
      content: `These Terms are governed by Indian law. Disputes resolved in New Delhi arbitration.`,
    },

    {
      title: "24. Modifications",
      content: `AICA may update these Terms. Continued use means acceptance.`,
    },

    {
      title: "25. Contact",
      content: `Email: a@aicahealth.com
Address: D-48, Ajay Enclave, New Delhi`,
    },

    {
      title: "26. Entire Agreement",
      content: `This document is the complete agreement between user and AICA.`,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-blue-100 relative overflow-hidden">

      {/* Background Glow */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-cyan-300/30 blur-3xl rounded-full"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-300/20 blur-3xl rounded-full"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* HEADER */}
        <div className="text-center mb-10">

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-md border border-cyan-100 mb-5">
            <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-cyan-700">
              AICA HEALTH LEGAL
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-bold text-slate-800">
            Terms & Conditions
          </h1>

          <p className="text-slate-600 mt-3 text-sm sm:text-base max-w-2xl mx-auto">
            MASTER TERMS & CONDITIONS — AICA HEALTH
          </p>
        </div>

        {/* CONTENT */}
        <div className="space-y-6">

          {sections.map((sec, i) => (
            <div
              key={i}
              className="
              bg-white/80
              backdrop-blur-xl
              border border-white/50
              rounded-3xl
              p-5 sm:p-7
              shadow-lg
              hover:shadow-2xl
              hover:-translate-y-1
              transition-all duration-300
            "
            >
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-4">
                {sec.title}
              </h2>

              <p className="text-slate-600 whitespace-pre-line leading-relaxed text-sm sm:text-base">
                {sec.content}
              </p>
            </div>
          ))}

        </div>

        {/* Bottom Actions */}
        <div className="mt-10 flex justify-center">

          <button
            onClick={() => navigate("/")}
            className="
            flex items-center gap-2
            px-6 py-3
            rounded-2xl
            bg-gradient-to-r from-cyan-500 to-blue-500
            text-white
            font-semibold
            shadow-lg
            hover:scale-105
            hover:shadow-cyan-300/50
            transition-all duration-300
          "
          >
            <ArrowLeft size={18} />
            Go Back
          </button>

        </div>

      </div>

      {/* FOOTER */}
      {/* <AppFooter /> */}
    </div>
  );
};

export default TermsAndConditions;